/**
 * ALERT FEED COMPONENT
 * 
 * Purpose:
 *   Displays a real-time feed of alerts with severity indicators.
 *   Used on dashboard homepage and alerts management page.
 * 
 * Features:
 *   - Real-time alert display (updates when new alerts received)
 *   - Severity-based color coding (CRITICAL=red, WARNING=yellow, INFO=blue)
 *   - User actions: resolve alert or dismiss temporarily
 *   - Clickable alerts for detailed investigation
 *   - Loading skeleton while data fetches
 *   - Empty state message when no alerts
 * 
 * Real-World Usage:
 *   1. WebSocket delivers new alert → triggers setAlerts() in App.tsx
 *   2. React re-renders AlertFeed with updated alerts array
 *   3. Component shows new alert at top of feed (with animation if implemented)
 *   4. User sees alert, clicks to investigate
 *   5. User resolves or dismisses, callback fired to parent
 *   6. Parent updates backend via API
 * 
 * Severity Color System:
 *   - CRITICAL (Red): Requires immediate action
 *   - WARNING (Yellow): Should be reviewed soon
 *   - INFO (Blue): Informational, for awareness
 * 
 * Performance Notes:
 *   - Only displays maxItems (default 10) to keep UI responsive
 *   - Rest of alerts available on full alerts page
 * 
 * Styling:
 *   - TailwindCSS utility classes for styling
 *   - Color dots indicate severity at-a-glance
 *   - Responsive padding and spacing
 */

import React from 'react';
import { Alert, AlertSeverity } from '../types';

/**
 * AlertFeedProps - Component props interface
 * 
 * @param alerts - Array of Alert objects to display
 * @param loading - Boolean indicating if alerts are being fetched
 * @param onResolve - Callback when user clicks "Resolve" (alert acknowledgment)
 * @param onDismiss - Callback when user clicks "Dismiss" (hide temporarily)
 * @param onAlertClick - Callback when user clicks alert for details/investigation
 * @param maxItems - Maximum number of alerts to display (default 10, rest on full alerts page)
 */
export interface AlertFeedProps {
  alerts: Alert[];
  loading?: boolean;
  onResolve?: (alertId: string) => void;
  onDismiss?: (alertId: string) => void;
  onAlertClick?: (alert: Alert) => void;
  maxItems?: number;
}

/**
 * Severity Color Mapping
 * 
 * Maps each severity level to Tailwind CSS classes for text/background color.
 * Used in alert card styling to provide visual hierarchy.
 * 
 * Design Pattern: Record<SeverityType, CSSClasses>
 * Makes it easy to update colors in one place if design changes.
 * 
 * Examples:
 *   - CRITICAL: Red (bg-red-100 = light red background, text-red-800 = dark red text)
 *   - WARNING: Yellow (attention needed soon)
 *   - INFO: Blue (informational context)
 */
const severityColors: Record<AlertSeverity, string> = {
  INFO: 'bg-blue-100 text-blue-800 border-blue-300',
  WARNING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  CRITICAL: 'bg-red-100 text-red-800 border-red-300',
};

/**
 * Severity Indicator Dot Colors
 * 
 * Small colored dots displayed at left of each alert for quick severity scan.
 * Solid colors (not text) to make severity instantly recognizable.
 * 
 * Why separate from severityColors?
 * - Text colors might need to be light background text
 * - Indicator dots should be fully saturated for visibility
 * 
 * Examples:
 *   - bg-red-500 = fully saturated red for CRITICAL alerting
 */
const severityBgDot = {
  INFO: 'bg-blue-500',
  WARNING: 'bg-yellow-500',
  CRITICAL: 'bg-red-500',
};

/**
 * AlertFeed React Component
 * 
 * Renders a list of alerts with:
 * - Severity color coding
 * - Action buttons (Resolve, Dismiss, Investigate)
 * - Loading skeleton while fetching
 * - Empty state when no alerts
 * 
 * Event Handling:
 *   - onResolve(): Mark alert as acknowledged/completed
 *   - onDismiss(): Hide alert (temporary, will reappear on refresh)
 *   - onAlertClick(): Open full alert details for investigation
 * 
 * Props Destructuring:
 *   - alerts: Required, array of alert objects
 *   - loading: UI state (show skeleton or actual alerts)
 *   - handlers: Optional callbacks, only called if provided
 *   - maxItems: Limits display (prevents overwhelming dashboard)
 */
export const AlertFeed: React.FC<AlertFeedProps> = ({
  alerts,
  loading = false,
  onResolve,
  onDismiss,
  onAlertClick,
  maxItems = 10,
}) => {
  /**
   * Slice alerts array to maxItems
   * 
   * Rationale:
   *   - Dashboard homepage shows only most important alerts
   *   - Users click "View All Alerts" to see complete list
   *   - Keeps dashboard from getting too crowded
   *   - Full alerts available on dedicated alerts page
   */
  const displayAlerts = alerts.slice(0, maxItems);

  /**
   * Loading State
   * 
   * While alerts are being fetched from API:
   * - Show 3 animated skeleton boxes (shimmer effect)
   * - Prevents jarring empty state before data loads
   * - Better UX than blank space or "Loading..." text
   * 
   * CSS: animate-pulse creates shimmer animation
   */
  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-gray-200 h-16 rounded"></div>
        ))}
      </div>
    );
  }

  /**
   * Empty State
   * 
   * When no alerts exist:
   * - Display friendly message "No alerts to display"
   * - Subtext "All systems operating normally"
   * - Reassures users that silence = good (no misinformation detected)
   * 
   * Common in monitoring dashboards - users expect some confirmation
   * that system is monitoring but found nothing to report.
   */
  if (displayAlerts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-sm">No alerts to display</p>
        <p className="text-xs mt-1">All systems operating normally</p>
      </div>
    );
  }

  /**
   * Alert List Render
   * 
   * For each alert, display card with:
   * - Colored left border indicating severity (red for CRITICAL, etc.)
   * - Alert message and metadata
   * - Timestamp when alert was triggered
   * - Action buttons (resolve, dismiss, investigate)
   * 
   * Each alert is clickable for full details view.
   */
  return (
    <div className="space-y-3">
      {displayAlerts.map((alert) => (
        <div
          key={alert.id}
          className={`
            p-4 rounded-lg border-l-4 cursor-pointer
            transition-all hover:shadow-md
            ${severityColors[alert.severity]}
          `}
          onClick={() => onAlertClick?.(alert)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-block w-2 h-2 rounded-full ${severityBgDot[alert.severity]}`}></span>
                <span className="font-semibold text-sm truncate">
                  {alert.alertType}
                </span>
                <span className="text-xs opacity-50 ml-auto">
                  {new Date(alert.createdAt).toLocaleTimeString()}
                </span>
              </div>

              <p className="text-sm leading-relaxed line-clamp-2">
                {alert.message}
              </p>

              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-xs bg-black bg-opacity-10 px-2 py-1 rounded">
                  Risk: {(alert.riskScore * 100).toFixed(0)}%
                </span>
                {alert.region && alert.region.length > 0 && (
                  <span className="text-xs bg-black bg-opacity-10 px-2 py-1 rounded">
                    {alert.region[0]}
                  </span>
                )}
              </div>
            </div>

            {!alert.resolved && (
              <div className="flex gap-2 flex-shrink-0">
                {onResolve && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onResolve(alert.id);
                    }}
                    className="px-2 py-1 text-xs font-medium rounded
                      bg-green-500 hover:bg-green-600 text-white
                      transition-colors"
                  >
                    Resolve
                  </button>
                )}
                {onDismiss && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(alert.id);
                    }}
                    className="px-2 py-1 text-xs font-medium rounded
                      bg-gray-400 hover:bg-gray-500 text-white
                      transition-colors"
                  >
                    Dismiss
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AlertFeed;
